import {useConfig} from '@aboutyou/config';
import {useGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {TargetGroup} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/target_group.types';
import {NewsletterSubscriptionService_SubscribeToWishlistNewsletterLazy} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.grpc.lazy';
import {Gender} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/newsletter_subscription/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {useContactRoute, usePrivacyPolicyRoute} from '@aboutyou/router';
import {
  useTrackComponent,
  useTrackComponentImpression,
} from '@aboutyou/tracking';
import {
  Button,
  COLORS,
  Disclaimer,
  FONT_SIZES,
  FONT_WEIGHTS,
  FilterFlyoutIconName,
  GenericIconName,
  Icon,
  RADIUS,
  RadioButton,
  RadioGroup,
  SPACING,
  Text,
  TextInput,
  desktopAndUpLinaria,
  useShowToast,
} from '@aboutyou/ui-components';
import {useCustomerSession, useOptionalUser} from '@aboutyou/user';
import {emailRegex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React from 'react';
import {useForm} from 'react-hook-form';
import {useSelectedTargetGroup} from '../../../../shared-deprecated/useSelectedTargetGroup';
import {MissionBadge} from '../../../Coins/MissionBadge/MissionBadge';
import backgroundDesktop from './assets/background-desktop.jpg';
import backgroundMobile from './assets/background-mobile.jpg';

interface FormData {
  email: string;
  gender: 'female' | 'male';
}

export const WishlistSubscribeToNewsletterBanner: React.FC = () => {
  const {device} = useConfig();
  const {t} = useTranslation();
  const optionalUser = useOptionalUser();
  const {targetGroup: selectedTargetGroup} = useSelectedTargetGroup();
  const session = useCustomerSession();
  const showToast = useShowToast();
  const privacyPolicyRoute = usePrivacyPolicyRoute();
  const contactRoute = useContactRoute();
  const trackComponent = useTrackComponent();
  const {ref} = useTrackComponentImpression({
    content_type: 'button',
    name: 'newsletter',
    context: '',
  });

  const subscribeToNewsletterCallback = useGrpcRequestCallback(
    NewsletterSubscriptionService_SubscribeToWishlistNewsletterLazy,
  );

  const {
    register,
    handleSubmit,
    getValues,
    formState: {errors, isSubmitting},
  } = useForm<FormData>({
    defaultValues: {
      email: optionalUser.data?.userData?.email,
      gender: selectedTargetGroup === TargetGroup.MALE ? 'male' : 'female',
    },
    mode: 'onSubmit',
  });

  const submitHandler = async ({email, gender}: FormData) => {
    try {
      if (!session) {
        return;
      }

      await subscribeToNewsletterCallback({
        email,
        gender: gender === 'male' ? Gender.MALE : Gender.FEMALE,
        session,
      });

      trackComponent({
        action: 'submit',
        content_type: 'button',
        name: 'newsletter',
        context: '',
      });

      showToast({
        content: (
          <SuccessMessageWrapper>
            <SuccessText
              size="px12"
              weight="mediumHeavy"
              dangerouslySetInnerHTML={{
                __html: t(
                  '<strong>Just one step left:</strong> To complete the process please click the link in the email',
                ),
              }}
            />
          </SuccessMessageWrapper>
        ),
        type: 'SUCCESS',
        'data-testid': 'subscribeToNewsletterSuccessMessage',
      });
    } catch (error) {
      showToast({
        content: t('Sorry, something went wrong! Please try again later.'),
        type: 'ERROR',
      });
    }
  };

  const chipIconSize = device === 'desktop' ? 'SS' : 'XS';

  return (
    <Wrapper ref={ref}>
      <Info>
        <Title color="White" textAlignment="left">
          {t('Stay updated with:')}
        </Title>
        <Chips>
          <div>
            <Icon
              asset={GenericIconName.Bell}
              size={chipIconSize}
              fill="White"
            />
            <ChipText color="White">{t('Price Drops')}</ChipText>
          </div>
          <div>
            <Icon
              asset={GenericIconName.Coupon}
              size={chipIconSize}
              fill="White"
            />
            <ChipText color="White">{t('Coupons')}</ChipText>
          </div>
          <div>
            <Icon
              asset={FilterFlyoutIconName.Shirt}
              size={chipIconSize}
              fill="White"
            />
            <ChipText color="White">{t('Low stock alerts')}</ChipText>
          </div>
        </Chips>
        <MissionBadge missionType="newsletterMission" />
      </Info>
      <Form
        autoComplete="on"
        onSubmit={handleSubmit(submitHandler)}
        method="POST"
        aria-label={t('Newsletter subscription form')}
      >
        <EmailInput
          type="email"
          autoComplete="email"
          value={getValues('email')}
          errorMessage={errors.email?.message}
          label={t('Your email address')}
          {...register('email', {
            required: t('Please enter your email address'),
            pattern: {
              value: emailRegex,
              message: t('This is not a valid email address'),
            },
          })}
          activeStyle={{
            backgroundColor: COLORS.White,
            borderColor: COLORS.Black,
          }}
        />

        <GenderContainer>
          <GenderLabel>{t('Receive exclusive offers')}</GenderLabel>

          <GenderRadioGroup>
            <GenderRadioButton
              label={t('For women')}
              {...register('gender')}
              value="female"
              id="newsletter-gender-female"
            />
            <GenderRadioButton
              label={t('For men')}
              {...register('gender')}
              value="male"
              id="newsletter-gender-male"
            />
          </GenderRadioGroup>
        </GenderContainer>

        <StyledDisclaimer
          text={t(
            'I would like to receive the ABOUT YOU newsletter on trends and offers (see {privacyPolicy}) by email. The consent can be withdrawn at any time via a newsletter email or customer service ({helpCenter}).',
            {
              privacyPolicy: `<a rel="noopener noreferrer" target="_blank" href="${privacyPolicyRoute}#section3.3.2.">${t(
                'Privacy Policy',
              )}</a>`,
              helpCenter: `<a rel="noopener noreferrer" target="_blank" href="${contactRoute}">${t(
                'Contact us | ABOUT YOU Help Center',
              )}</a>`,
            },
          )}
        />

        <Button variant="secondary" isLoading={isSubmitting}>
          {t('Sign up for newsletter')}
        </Button>
      </Form>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  background-image: url(${backgroundMobile});
  background-size: cover;
  background-position: top center;
  background-repeat: no-repeat;
  width: 100%;
  padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
  border-radius: ${RADIUS.Sm};
  gap: ${SPACING['Y.Xl2']};
  margin-bottom: ${SPACING['Y.Xl']};

  ${desktopAndUpLinaria(`
    flex-direction: row;
    justify-content: space-between;
    background-image: url(${backgroundDesktop});
    padding: ${SPACING['Y.Xl']} ${SPACING['X.Xl']};
    gap: unset;
  `)}
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  width: 60%;

  ${desktopAndUpLinaria(`
    width: 26%;
  `)}
`;

const Title = styled(Text)`
  font-size: ${FONT_SIZES.px18};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.5;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${SPACING['X.Sm']} ${SPACING['Y.Lg']};
  padding: ${SPACING['Y.Sm']} 0 ${SPACING['Y.Lg']} 0;

  ${desktopAndUpLinaria(`
    gap: ${SPACING['X.Sm']}; ${SPACING['Y.Md']};
    padding-block: ${SPACING['Y.Xl']};
  `)}

  > div {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    height: 26px;

    ${desktopAndUpLinaria(`
      padding-inline: ${SPACING['X.Md']};
      height: 32px;
    `)}
  }
`;

const ChipText = styled(Text)`
  font-size: ${FONT_SIZES.px12};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  white-space: nowrap;
  line-height: 1.5;

  ${desktopAndUpLinaria(`
    line-height: 2;
  `)}
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;

  ${desktopAndUpLinaria(`
    flex: 0 1 50%;
    background-color: ${COLORS['Fill.White']};
    padding: ${SPACING['Y.Lg']} ${SPACING['X.Lg']};
    border-radius: ${RADIUS.Sm};
  `)}
`;

const EmailInput = styled(TextInput)`
  display: none;

  ${desktopAndUpLinaria(`
    display: block;
    flex-grow: 0;
  `)}
`;

const GenderContainer = styled.div`
  display: none;

  ${desktopAndUpLinaria(`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    padding: 0;
    margin-top: -${SPACING['Y.Md']};
  `)}
`;

const GenderLabel = styled(Text)`
  display: block;
  font-size: ${FONT_SIZES.px14};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.4;

  ${desktopAndUpLinaria(`
    font-weight: ${FONT_WEIGHTS.mediumBold};
  `)}
`;

const GenderRadioButton = styled(RadioButton)`
  padding: 12px 0;
`;

const GenderRadioGroup = styled(RadioGroup)`
  ${GenderRadioButton} + ${GenderRadioButton} {
    margin-left: 12px;
  }
`;

const StyledDisclaimer = styled(Disclaimer)`
  display: none;

  ${desktopAndUpLinaria(`
    display: block;
    font-size: ${FONT_SIZES.px10};
    text-align: left;
  `)}
`;

const SuccessMessageWrapper = styled.span`
  display: flex;
  gap: 8px;
`;

const SuccessText = styled(Text)`
  line-height: 1.5;
  text-align: left;
`;
